import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Loader2, Star, CheckCircle2, TrendingUp, ArrowRight, Mail, ChevronsUpDown, Check } from 'lucide-react';
import { estimationAPI, marketAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import SEO from '../components/SEO';
import { useTranslation } from 'react-i18next';

const BRANDS = ['Bullpadel', 'Head', 'Nox', 'Babolat', 'Wilson', 'Adidas', 'Siux', 'StarVie', 'Varlion', 'Drop Shot', 'Autre'];

const CONDITIONS = [
  { value: 'neuf_etiquette', icon: '✦', color: 'bg-emerald-500' },
  { value: 'excellent', icon: '✦', color: 'bg-green-500' },
  { value: 'bon', icon: '✦', color: 'bg-blue-500' },
  { value: 'correct', icon: '✦', color: 'bg-amber-500' },
  { value: 'use', icon: '✦', color: 'bg-red-500' },
];

const DEFECTS = ['rayures_legeres', 'rayures_profondes', 'chocs_cadre', 'grip_use', 'tamis_abime', 'mousse_decollee', 'peinture_ecaillee'];

const ACCESSORIES = ['housse', 'surgrip', 'protection_cadre', 'boite_origine'];

const YEARS = Array.from({ length: 10 }, (_, i) => 2026 - i);

const stripBrand = (model, brand) => {
  if (!model || !brand) return model;
  const regex = new RegExp('^' + brand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s+', 'i');
  return model.replace(regex, '');
};

export default function EstimatePage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    brand: '', model: '', year: null, condition: '', defects: [], accessories: [], email: '',
  });

  // Model combobox state
  const [brandsModels, setBrandsModels] = useState({});
  const [availableModels, setAvailableModels] = useState([]);
  const [modelOpen, setModelOpen] = useState(false);
  const [customModel, setCustomModel] = useState(false);

  // Load brands/models data
  useEffect(() => {
    marketAPI.getBrandsModels().then(res => setBrandsModels(res.data)).catch(() => {});
  }, []);

  // Update available models when brand changes
  useEffect(() => {
    if (form.brand && form.brand !== 'Autre' && brandsModels[form.brand.toLowerCase()]) {
      setAvailableModels(brandsModels[form.brand.toLowerCase()] || []);
    } else {
      setAvailableModels([]);
    }
    setForm(prev => ({ ...prev, model: '' }));
    setCustomModel(form.brand === 'Autre');
  }, [form.brand, brandsModels]);

  const toggleItem = (key, value) => {
    setForm(prev => ({
      ...prev,
      [key]: prev[key].includes(value) ? prev[key].filter(v => v !== value) : [...prev[key], value],
    }));
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const canNext = () => {
    if (step === 0) return form.brand;
    if (step === 1) return form.model.length >= 2;
    if (step === 2) return true;
    if (step === 3) return form.condition;
    if (step === 4) return true;
    if (step === 5) return true;
    if (step === 6) return isValidEmail(form.email);
    return true;
  };

  const handleEstimate = async () => {
    setLoading(true);
    try {
      const res = await estimationAPI.estimate({ ...form, language: i18n.language });
      setResult(res.data);
      setStep(7);
    } catch {
      setResult({ recommended_price: 50, min_price: 30, max_price: 80, ai_explanation: t('est.fallback_explanation') });
      setStep(7);
    }
    setLoading(false);
  };

  const handleSell = () => {
    if (isAuthenticated) {
      navigate('/vendre', { state: { prefill: { brand: form.brand, model: form.model, estimated_price: result?.recommended_price } } });
    } else {
      navigate('/register', { state: { from: '/vendre', prefill: { brand: form.brand, model: form.model } } });
    }
  };

  const progress = Math.min(((step) / 7) * 100, 100);
  const selectedModelClean = stripBrand(form.model, form.brand);

  const renderStep = () => {
    switch (step) {
      case 0: return (
        <div data-testid="step-brand">
          <h2 className="text-lg font-bold text-[#033871] mb-1">{t('est.step_brand_title')}</h2>
          <p className="text-sm text-[#5A8BA8] mb-5">{t('est.step_brand_sub')}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {BRANDS.map(b => (
              <button key={b} onClick={() => setForm(p => ({ ...p, brand: b }))}
                className={`p-3.5 rounded-xl border-2 text-sm font-semibold transition-all ${form.brand === b ? 'border-[#0C63BF] bg-[#0C63BF]/5 text-[#0C63BF]' : 'border-[#E8F4FD] text-[#033871] hover:border-[#B8D4E3]'}`}
                data-testid={`brand-${b}`}>
                {b === 'Autre' ? t('est.brand_other') : b}
              </button>
            ))}
          </div>
        </div>
      );
      case 1: return (
        <div data-testid="step-model">
          <h2 className="text-lg font-bold text-[#033871] mb-1">{t('est.step_model_title', { brand: form.brand === 'Autre' ? t('est.brand_other') : form.brand })}</h2>
          <p className="text-sm text-[#5A8BA8] mb-5">
            {customModel || form.brand === 'Autre' 
              ? t('est.type_model_name') 
              : t('est.search_in_list')}
          </p>
          
          {!customModel && form.brand !== 'Autre' ? (
            <>
              <Popover open={modelOpen} onOpenChange={setModelOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between font-normal h-12 text-base" data-testid="model-select">
                    {selectedModelClean || t('est.search_model_ph')}
                    <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder={t('est.search_cmd_ph', { brand: form.brand })} />
                    <CommandList>
                      <CommandEmpty>{t('est.no_model')}</CommandEmpty>
                      <CommandGroup heading={t('est.models_count', { count: availableModels.length })}>
                        {availableModels.map(m => {
                          const cleanName = stripBrand(m.display, form.brand);
                          return (
                            <CommandItem key={m.key} value={m.display}
                              onSelect={v => { setForm(prev => ({ ...prev, model: v })); setModelOpen(false); }}>
                              <Check className={`mr-2 h-4 w-4 ${form.model === m.display ? 'opacity-100' : 'opacity-0'}`} />
                              {cleanName}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <button
                type="button"
                onClick={() => { setCustomModel(true); setForm(prev => ({ ...prev, model: '' })); }}
                className="mt-3 text-xs text-[#0C63BF] hover:underline"
                data-testid="custom-model-toggle"
              >
                {t('est.not_in_list')}
              </button>
            </>
          ) : (
            <>
              <input
                value={form.model}
                onChange={e => setForm(p => ({ ...p, model: e.target.value }))}
                placeholder={t('est.model_input_ph')}
                autoFocus
                className="w-full h-12 px-4 rounded-xl border-2 border-[#D6E8F0] text-[#033871] text-base font-medium focus:border-[#0C63BF] focus:ring-1 focus:ring-[#0C63BF] outline-none"
                data-testid="model-input"
              />
              {form.brand !== 'Autre' && (
                <button
                  type="button"
                  onClick={() => { setCustomModel(false); setForm(prev => ({ ...prev, model: '' })); }}
                  className="mt-3 text-xs text-[#0C63BF] hover:underline"
                  data-testid="back-to-list-toggle"
                >
                  {t('est.back_to_list')}
                </button>
              )}
            </>
          )}
        </div>
      );
      case 2: return (
        <div data-testid="step-year">
          <h2 className="text-lg font-bold text-[#033871] mb-1">{t('est.step_year_title')}</h2>
          <p className="text-sm text-[#5A8BA8] mb-5">{t('est.step_year_sub')}</p>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2.5">
            {YEARS.map(y => (
              <button key={y} onClick={() => setForm(p => ({ ...p, year: y }))}
                className={`p-3 rounded-xl border-2 text-sm font-semibold transition-all ${form.year === y ? 'border-[#0C63BF] bg-[#0C63BF]/5 text-[#0C63BF]' : 'border-[#E8F4FD] text-[#033871] hover:border-[#B8D4E3]'}`}
                data-testid={`year-${y}`}>
                {y}
              </button>
            ))}
            <button onClick={() => setForm(p => ({ ...p, year: null }))}
              className={`p-3 rounded-xl border-2 text-sm font-semibold transition-all ${form.year === null ? 'border-[#0C63BF] bg-[#0C63BF]/5 text-[#0C63BF]' : 'border-[#E8F4FD] text-[#5A8BA8] hover:border-[#B8D4E3]'}`}>
              {t('est.dont_know')}
            </button>
          </div>
        </div>
      );
      case 3: return (
        <div data-testid="step-condition">
          <h2 className="text-lg font-bold text-[#033871] mb-1">{t('est.step_cond_title')}</h2>
          <p className="text-sm text-[#5A8BA8] mb-5">{t('est.step_cond_sub')}</p>
          <div className="space-y-2.5">
            {CONDITIONS.map(c => (
              <button key={c.value} onClick={() => setForm(p => ({ ...p, condition: c.value }))}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${form.condition === c.value ? 'border-[#0C63BF] bg-[#0C63BF]/5' : 'border-[#E8F4FD] hover:border-[#B8D4E3]'}`}
                data-testid={`condition-${c.value}`}>
                <span className={`w-8 h-8 rounded-lg ${c.color} flex items-center justify-center text-white text-xs font-bold`}>{c.icon}</span>
                <div>
                  <p className="text-sm font-semibold text-[#033871]">{t(`est.cond_${c.value}`)}</p>
                  <p className="text-xs text-[#5A8BA8]">{t(`est.cond_${c.value}_desc`)}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      );
      case 4: return (
        <div data-testid="step-defects">
          <h2 className="text-lg font-bold text-[#033871] mb-1">{t('est.step_def_title')}</h2>
          <p className="text-sm text-[#5A8BA8] mb-5">{t('est.step_def_sub')}</p>
          
          {/* Aucun défaut option */}
          <button 
            onClick={() => setForm(p => ({ ...p, defects: [] }))}
            className={`w-full p-3.5 rounded-xl border-2 text-sm font-medium text-left transition-all flex items-center gap-2.5 mb-4 ${form.defects.length === 0 ? 'border-[#32CD32] bg-green-50 text-green-700' : 'border-[#E8F4FD] text-[#033871] hover:border-[#B8D4E3]'}`}
            data-testid="defect-none">
            <span className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${form.defects.length === 0 ? 'border-[#32CD32] bg-[#32CD32]' : 'border-[#D6E8F0]'}`}>
              {form.defects.length === 0 && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
            </span>
            {t('est.no_defect')}
          </button>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {DEFECTS.map(d => (
              <button key={d} onClick={() => toggleItem('defects', d)}
                className={`p-3.5 rounded-xl border-2 text-sm font-medium text-left transition-all flex items-center gap-2.5 ${form.defects.includes(d) ? 'border-red-400 bg-red-50 text-red-700' : 'border-[#E8F4FD] text-[#033871] hover:border-[#B8D4E3]'}`}
                data-testid={`defect-${d}`}>
                <span className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${form.defects.includes(d) ? 'border-red-400 bg-red-400' : 'border-[#D6E8F0]'}`}>
                  {form.defects.includes(d) && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                </span>
                {t(`est.def_${d}`)}
              </button>
            ))}
          </div>
        </div>
      );
      case 5: return (
        <div data-testid="step-accessories">
          <h2 className="text-lg font-bold text-[#033871] mb-1">{t('est.step_acc_title')}</h2>
          <p className="text-sm text-[#5A8BA8] mb-5">{t('est.step_acc_sub')}</p>
          
          {/* Aucun accessoire option */}
          <button 
            onClick={() => setForm(p => ({ ...p, accessories: [] }))}
            className={`w-full p-3.5 rounded-xl border-2 text-sm font-medium text-left transition-all flex items-center gap-2.5 mb-4 ${form.accessories.length === 0 ? 'border-[#0C63BF] bg-[#0C63BF]/5 text-[#0C63BF]' : 'border-[#E8F4FD] text-[#033871] hover:border-[#B8D4E3]'}`}
            data-testid="accessory-none">
            <span className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${form.accessories.length === 0 ? 'border-[#0C63BF] bg-[#0C63BF]' : 'border-[#D6E8F0]'}`}>
              {form.accessories.length === 0 && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
            </span>
            {t('est.no_accessory')}
          </button>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {ACCESSORIES.map(a => (
              <button key={a} onClick={() => toggleItem('accessories', a)}
                className={`p-3.5 rounded-xl border-2 text-sm font-medium text-left transition-all flex items-center gap-2.5 ${form.accessories.includes(a) ? 'border-[#32CD32] bg-green-50 text-green-700' : 'border-[#E8F4FD] text-[#033871] hover:border-[#B8D4E3]'}`}
                data-testid={`accessory-${a}`}>
                <span className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${form.accessories.includes(a) ? 'border-[#32CD32] bg-[#32CD32]' : 'border-[#D6E8F0]'}`}>
                  {form.accessories.includes(a) && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                </span>
                {t(`est.acc_${a}`)}
              </button>
            ))}
          </div>
        </div>
      );
      case 6: return (
        <div data-testid="step-email">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0C63BF] to-[#033871] flex items-center justify-center mx-auto mb-4">
            <Mail className="w-7 h-7 text-white" />
          </div>
          <h2 className="text-lg font-bold text-[#033871] mb-1 text-center">{t('est.step_email_title')}</h2>
          <p className="text-sm text-[#5A8BA8] mb-6 text-center">
            {t('est.step_email_sub', { racket: `${form.brand} ${selectedModelClean}` })}
          </p>
          <input
            type="email"
            value={form.email}
            onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
            placeholder={t('est.email_ph')}
            autoFocus
            className="w-full h-12 px-4 rounded-xl border-2 border-[#D6E8F0] text-[#033871] text-base font-medium focus:border-[#0C63BF] focus:ring-1 focus:ring-[#0C63BF] outline-none text-center"
            data-testid="email-input"
          />
          <p className="text-xs text-[#B8D4E3] mt-3 text-center">
            {t('est.email_hint')}
          </p>
        </div>
      );
      case 7: return result && (
        <div className="text-center" data-testid="step-result">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0C63BF] to-[#033871] flex items-center justify-center mx-auto mb-4">
            <TrendingUp className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-lg font-bold text-[#033871] mb-1">{t('est.result_title', { racket: `${form.brand} ${selectedModelClean}` })}</h2>
          <p className="text-sm text-[#5A8BA8] mb-6">{t('est.result_sub')}</p>

          <div className="bg-gradient-to-br from-[#033871] to-[#0C63BF] rounded-2xl p-6 mb-5 text-white">
            <p className="text-sm text-white/70 mb-1">{t('est.recommended_price')}</p>
            <p className="text-5xl font-black mb-2" data-testid="estimated-price">{result.recommended_price}€</p>
            <div className="flex items-center justify-center gap-4 text-sm text-white/70">
              <span>{t('est.min', { price: result.min_price })}</span>
              <span className="w-1 h-1 rounded-full bg-white/30" />
              <span>{t('est.max', { price: result.max_price })}</span>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-5 flex items-center gap-2 justify-center">
            <Mail className="w-4 h-4 text-green-600" />
            <p className="text-sm text-green-700">{t('est.sent_to')} <strong>{form.email}</strong></p>
          </div>

          {result.ai_explanation && (
            <div className="bg-[#F0F7FA] rounded-xl p-4 mb-5 text-left">
              <p className="text-xs font-semibold text-[#033871] mb-1">{t('est.ai_analysis')}</p>
              <p className="text-sm text-[#5A8BA8]">{result.ai_explanation}</p>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3 mb-6">
            {result.market_reference_price && (
              <div className="bg-white rounded-xl border border-[#E8F4FD] p-3">
                <p className="text-[10px] text-[#5A8BA8]">{t('est.new_price')}</p>
                <p className="text-base font-bold text-[#033871]">{result.market_reference_price}€</p>
              </div>
            )}
            {result.similar_sold_avg && (
              <div className="bg-white rounded-xl border border-[#E8F4FD] p-3">
                <p className="text-[10px] text-[#5A8BA8]">{t('est.sold', { count: result.similar_sold_count })}</p>
                <p className="text-base font-bold text-[#033871]">{result.similar_sold_avg}€</p>
              </div>
            )}
            {result.similar_active_avg && (
              <div className="bg-white rounded-xl border border-[#E8F4FD] p-3">
                <p className="text-[10px] text-[#5A8BA8]">{t('est.on_sale', { count: result.similar_active_count })}</p>
                <p className="text-base font-bold text-[#033871]">{result.similar_active_avg}€</p>
              </div>
            )}
          </div>

          <Button onClick={handleSell} className="w-full h-12 bg-[#32CD32] hover:bg-[#28a428] text-white font-bold text-base rounded-xl gap-2" data-testid="sell-btn">
            {t('est.sell_btn')} <ArrowRight className="w-5 h-5" />
          </Button>
          <p className="text-xs text-[#5A8BA8] mt-3">
            {isAuthenticated ? t('est.redirect_hint') : t('est.create_account_hint')}
          </p>
        </div>
      );
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F7FA]">
      <SEO title={t('est.seo_title')} description={t('est.seo_desc')} path="/estimer" />

      <div className="max-w-lg mx-auto px-4 py-8 sm:py-12">
        {/* Header */}
        {step < 7 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <h1 className="text-xl font-heading font-bold text-[#033871]">{t('est.title')}</h1>
              <span className="text-xs text-[#5A8BA8] font-medium">{step + 1}/7</span>
            </div>
            <div className="h-1.5 bg-[#E8F4FD] rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#0C63BF] to-[#32CD32] rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        {/* Step content */}
        <div className="bg-white rounded-2xl border border-[#E8F4FD] p-5 sm:p-6 shadow-sm">
          {loading ? (
            <div className="flex flex-col items-center py-12" data-testid="loading-estimation">
              <Loader2 className="w-10 h-10 text-[#0C63BF] animate-spin mb-4" />
              <p className="text-sm font-semibold text-[#033871]">{t('est.estimating')}</p>
              <p className="text-xs text-[#5A8BA8] mt-1">{t('est.ai_analyzing')}</p>
            </div>
          ) : renderStep()}
        </div>

        {/* Navigation */}
        {step < 7 && !loading && (
          <div className="flex items-center justify-between mt-5">
            <Button variant="ghost" onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} className="gap-1.5 text-[#5A8BA8]" data-testid="prev-btn">
              <ChevronLeft className="w-4 h-4" /> {t('est.back')}
            </Button>
            {step < 6 ? (
              <Button onClick={() => setStep(s => s + 1)} disabled={!canNext()} className="bg-[#0C63BF] text-white gap-1.5" data-testid="next-btn">
                {t('est.next')} <ChevronRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button onClick={handleEstimate} disabled={!canNext()} className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white gap-1.5" data-testid="estimate-btn">
                {t('est.get_estimate')} <Star className="w-4 h-4" />
              </Button>
            )}
          </div>
        )}

        {step === 7 && !loading && (
          <button onClick={() => { setStep(0); setResult(null); setForm({ brand: '', model: '', year: null, condition: '', defects: [], accessories: [], email: '' }); }}
            className="w-full mt-4 text-sm text-[#5A8BA8] hover:text-[#033871] font-medium" data-testid="restart-btn">
            {t('est.restart')}
          </button>
        )}
      </div>
    </div>
  );
}
